import { ArrowRightLeft, Gauge } from "lucide-react";
import { useMemo } from "react";
import { NumericInput } from "../../components/NumericInput";
import { copy } from "../../data/i18n";
import { compactDecimalsFor, decimalsFor, toDisplayValue, unitFor } from "../../data/units";
import type { Language, UnitSystem } from "../../types/preferences";
import { formatNumber } from "../../utils/numberFormat";
import { calculateWireConversion } from "./formulas";
import type { WireConversionInputs } from "./types";

type WireConversionCalculatorProps = {
  language: Language;
  unitSystem: UnitSystem;
  inputs: WireConversionInputs;
  inputText: Record<keyof WireConversionInputs, string>;
  onInputTextChange: (key: keyof WireConversionInputs, value: string) => void;
};

export function WireConversionCalculator({
  language,
  unitSystem,
  inputs,
  inputText,
  onInputTextChange
}: WireConversionCalculatorProps) {
  const t = copy[language];
  const locale = language === "de" ? "de-DE" : "en-US";
  const result = useMemo(() => calculateWireConversion(inputs), [inputs]);
  const targetWireFeed = toDisplayValue("wireFeed", result.targetWireFeed, unitSystem);
  const targetWireFeedDecimals = decimalsFor("wireFeed", unitSystem);

  return (
    <section className="calculator-panel" aria-label={t.modules.wireConversion}>
      <div className="calculator-heading">
        <div>
          <h2>{t.modules.wireConversion}</h2>
          <span>{t.modules.wireConversionDescription}</span>
        </div>
      </div>

      <div className="calculator-body conversion-body">
        <div className="parameter-section">
          <div className="section-label">{t.inputParameters}</div>
          <div className="input-grid">
            <NumericInput
              label={t.wireConversion.sourceDiameter}
              shortLabel={t.wireConversion.sourceShort}
              unit={unitFor("wireDiameter", unitSystem)}
              value={toDisplayValue("wireDiameter", inputs.sourceDiameter, unitSystem)}
              textValue={inputText.sourceDiameter}
              decimals={decimalsFor("wireDiameter", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("sourceDiameter", value)}
            />
            <NumericInput
              label={t.wireConversion.sourceWireFeed}
              shortLabel="vD"
              unit={unitFor("wireFeed", unitSystem)}
              value={toDisplayValue("wireFeed", inputs.sourceWireFeed, unitSystem)}
              textValue={inputText.sourceWireFeed}
              decimals={decimalsFor("wireFeed", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("sourceWireFeed", value)}
            />
            <NumericInput
              label={t.wireConversion.targetDiameter}
              shortLabel={t.wireConversion.targetShort}
              unit={unitFor("wireDiameter", unitSystem)}
              value={toDisplayValue("wireDiameter", inputs.targetDiameter, unitSystem)}
              textValue={inputText.targetDiameter}
              decimals={decimalsFor("wireDiameter", unitSystem)}
              locale={locale}
              onChange={(value) => onInputTextChange("targetDiameter", value)}
            />
          </div>
        </div>

        <aside className="result-panel conversion-result" aria-live="polite">
          <div className="result-header">
            <span>
              <ArrowRightLeft size={16} /> {t.result}
            </span>
          </div>
          <p className="result-label">{t.wireConversion.targetWireFeed}</p>
          <div className="result-value">
            {formatNumber(targetWireFeed, targetWireFeedDecimals, locale)}
            <span>{unitFor("wireFeed", unitSystem)}</span>
          </div>

          <div className="formula-box">
            <span>
              <Gauge size={15} /> {t.formula}
            </span>
            <code>vD target = vD source * source area / target area</code>
          </div>

          <div className="detail-grid">
            <div>
              <span>{t.wireConversion.sourceArea}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("area", result.sourceArea, unitSystem),
                  compactDecimalsFor("area", unitSystem, toDisplayValue("area", result.sourceArea, unitSystem)),
                  locale
                )}{" "}
                {unitFor("area", unitSystem)}
              </strong>
            </div>
            <div>
              <span>{t.wireConversion.targetArea}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("area", result.targetArea, unitSystem),
                  compactDecimalsFor("area", unitSystem, toDisplayValue("area", result.targetArea, unitSystem)),
                  locale
                )}{" "}
                {unitFor("area", unitSystem)}
              </strong>
            </div>
            <div>
              <span>{t.wireConversion.areaRatio}</span>
              <strong>{formatNumber(result.ratio, 3, locale)}</strong>
            </div>
            <div>
              <span>{t.labels.volumeRate}</span>
              <strong>
                {formatNumber(
                  toDisplayValue("volumeRate", result.volumeRate, unitSystem),
                  compactDecimalsFor("volumeRate", unitSystem, toDisplayValue("volumeRate", result.volumeRate, unitSystem)),
                  locale
                )}{" "}
                {unitFor("volumeRate", unitSystem)}
              </strong>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
